import React, { useEffect, useEffectEvent, useState } from 'react';
import SMActiveReviewTabActionBar, { SMActiveReviewTabTab } from './active-review-tab-action-bar';
import ConfirmCloseReviewDialog from './confirm-close-review-dialog';
import styles from './index.module.scss';
import SMActiveReviewTabActivityTab from './tabs/activity';
import SMActiveReviewTabReviewPlanTab from './tabs/review-plan';
import SMActiveReviewTabSummaryTab from './tabs/summary';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import { InfoField } from '@components-clean/core/info-field';
import Loader from '@components-clean/core/loader';
import { useUnsavedChangesConfirmation } from '@hooks-clean/index';
// eslint-disable-next-line max-len
import { COMPONENT_NAME as EDIT_REVIEW_PLAN_SECTION_COMPONENT_NAME } from '@pages-clean/subject-monitoring/components/monitoring-toolkit/_shared/review-plan-details';
// eslint-disable-next-line max-len
import { COMPONENT_NAME as SUMMARY_SECTION_COMPONENT_NAME } from '@pages-clean/subject-monitoring/components/monitoring-toolkit/_shared/review-summary-card';
import { useBoolean, useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import { SubjectMonitoringStudyMemberApi } from '@apis/bff-api/subject-monitoring/subject-monitoring-study-member-api';
import { SubjectReviewInstanceApi } from '@apis/bff-api/subject-monitoring/subject-review-instance';
import { SubjectMonitoringReviewAPI } from '@apis/bff-api/subject-monitoring-review';

type Props = {
    isHalfScreen: boolean;
    studyRef: string;
    reviewRecordId: number;
    sessionId: string;
    exitReviewMode: () => void;
};

// Component names to watch for changes (to show unsaved changes confirmation)
const COMPONENT_NAMES = [EDIT_REVIEW_PLAN_SECTION_COMPONENT_NAME, SUMMARY_SECTION_COMPONENT_NAME];

const SMActiveReviewTab = ({ isHalfScreen, studyRef, reviewRecordId, sessionId, exitReviewMode }: Props) => {
    const { t } = useTranslation('sm', { keyPrefix: 'monitoringToolkit.activeReviewTab' });
    const { t: tCommon } = useTranslation('common');

    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'subject-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAMES,
    });

    const [selectedTab, setSelectedTab] = useState<SMActiveReviewTabTab>('review-plan');

    const confirmCloseReviewDialogOpen = useBoolean(false);

    const getReviewQuery = SubjectMonitoringReviewAPI.useGetByRecordIdQuery(studyRef, reviewRecordId);
    const getReviewSubjectsQuery = SubjectReviewInstanceApi.useGetSubjects(studyRef, reviewRecordId);
    const getSmStudyMembersQuery = SubjectMonitoringStudyMemberApi.useGetAllStudyMembers(studyRef, true);

    const review = getReviewQuery.data;
    const reviewSubjects = getReviewSubjectsQuery.data;
    const smStudyMembers = getSmStudyMembersQuery.data;

    const closeReviewMutation = SubjectMonitoringReviewAPI.useCloseReviewMutation(studyRef, reviewRecordId);

    function getTabContent() {
        switch (selectedTab) {
            case 'review-plan':
                return (
                    review != null &&
                    reviewSubjects != null &&
                    smStudyMembers != null && (
                        <SMActiveReviewTabReviewPlanTab
                            isHalfScreen={isHalfScreen}
                            studyRef={studyRef}
                            review={review}
                            reviewSubjects={reviewSubjects}
                            smStudyMembers={smStudyMembers}
                            sessionId={sessionId}
                        />
                    )
                );
            case 'activity':
                return (
                    review != null &&
                    reviewSubjects != null && (
                        <SMActiveReviewTabActivityTab
                            studyRef={studyRef}
                            review={review}
                            reviewSubjects={reviewSubjects}
                        />
                    )
                );
            case 'summary':
                return (
                    review != null && (
                        <SMActiveReviewTabSummaryTab isHalfScreen={isHalfScreen} studyRef={studyRef} review={review} />
                    )
                );

            default:
                return null;
        }
    }

    const isLoading = getReviewQuery.isLoading || getReviewSubjectsQuery.isLoading || getSmStudyMembersQuery.isLoading;
    const isError = !!getReviewQuery.error || !!getReviewSubjectsQuery.error || !!getSmStudyMembersQuery.error;

    // When the unsaved changes context is cleared, the toolkit component needs to rerender to detect that it no longer needs to show the confirmation
    // If `exitEditMode` is called during this time, the confirmation is still shown (which we don't want).
    // Additionally, if the confirmation is then cancelled, an error is thrown.
    // ---
    // So we delay calling `exitEditMode` until the next render, at which point the toolkit component will no longer show the confirmation.
    const [readyToExit, setReadyToExit] = useState(false);
    const exitReviewModeEvent = useEffectEvent(exitReviewMode);
    useEffect(() => {
        if (readyToExit) {
            exitReviewModeEvent();
        }
    }, [readyToExit]);

    const onExitClicked = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        setReadyToExit(true);
    };

    const closeReview = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        try {
            await closeReviewMutation.trigger({ sessionId });
            setReadyToExit(true);
            ShowSuccess(tCommon('actionsSuccessfulToast'));
        } catch {
            ShowError(tCommon('errorMessage'));
        }
    };

    return (
        <>
            <Card
                data-testid="active-review-tab-content"
                title={
                    <SMActiveReviewTabActionBar
                        reviewRecordId={reviewRecordId}
                        selectedTab={selectedTab}
                        setSelectedTab={setSelectedTab}
                    />
                }
                headerActions={
                    <div className={styles.headerActions}>
                        <Button data-testid="exit-review-mode-button" onClick={onExitClicked}>
                            {t('actions.exitReviewMode')}
                        </Button>
                        <Button
                            data-testid="exit-review-mode-button"
                            onClick={confirmCloseReviewDialogOpen.setTrue}
                            primary
                        >
                            {t('actions.closeReview')}
                        </Button>
                    </div>
                }
                className={styles.tabContent}
                bodyClassName={styles.tabContentInner}
            >
                {isLoading || isError ? (
                    <div className={styles.cardLoadingStateWrapper}>
                        {isError ? (
                            <InfoField type="Error" message={tCommon('errorMessage')} />
                        ) : isLoading ? (
                            <Loader />
                        ) : null}
                    </div>
                ) : (
                    getTabContent()
                )}
            </Card>

            {confirmCloseReviewDialogOpen.value && review?.title != null && (
                <ConfirmCloseReviewDialog
                    reviewTitle={review.title}
                    onCancel={confirmCloseReviewDialogOpen.setFalse}
                    onConfirm={closeReview}
                />
            )}
        </>
    );
};

export default SMActiveReviewTab;
